/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: QuickListModal.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Modal component for quick list search in System Misc.
────────────────────────────────────────────────────────────────*/

import { Search } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import { SystemMiscConfig } from '../types';

interface QuickListModalProps {
  show: boolean;
  search: string;
  configs: SystemMiscConfig[];
  isLoading: boolean;
  onSearchChange: (value: string) => void;
  onSelect: (config: SystemMiscConfig) => void;
  onClose: () => void;
}

export function QuickListModal({
  show,
  search,
  configs,
  isLoading,
  onSearchChange,
  onSelect,
  onClose,
}: QuickListModalProps) {
  const { t } = useI18n();

  if (!show) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border border-border bg-card shadow-xl">
        <div className="flex items-center justify-between border-b border-border p-4">
          <h3 className="text-lg font-semibold text-foreground">{t('systemMisc.quickListTitle')}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('systemMisc.quickListClose')}
            className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            ×
          </button>
        </div>

        <div className="space-y-4 p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder={t('systemMisc.searchConfigurations')}
              value={search}
              onChange={(event) => onSearchChange(event.target.value)}
              className="w-full rounded-md border border-border bg-background px-10 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          <div className="max-h-64 space-y-2 overflow-y-auto">
            {isLoading ? (
              <div className="py-8 text-center text-muted-foreground">{t('common.loading')}</div>
            ) : configs.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">
                {search ? t('systemMisc.noConfigurationsFound') : t('systemMisc.noConfigurations')}
              </div>
            ) : (
              configs.map((config) => (
                <button
                  key={config.id}
                  type="button"
                  onClick={() => onSelect(config)}
                  className="w-full rounded-md p-3 text-left transition-colors hover:bg-accent"
                >
                  <div className="font-medium text-foreground">{config.name}</div>
                  <div className="text-sm text-muted-foreground">
                    {config.timezone ?? t('systemMisc.unknown')} • {config.logLevel ?? t('systemMisc.unknown')}
                  </div>
                  {config.server && (
                    <div className="mt-1 text-xs text-muted-foreground">
                      {config.server.name} ({config.server.host})
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

